import * as React from 'react'
import classNames from 'classnames'

/**
 * Drop-in replacement for `Card` from `@blueprintjs/core`.
 *
 * Matches Blueprint's API surface so callsites can switch by changing the
 * import. Visuals match Blueprint exactly — same default 20px padding, same
 * 6px border-radius (Blueprint's stock value is 2px but `app.scss` overrides
 * `.bp4-card { border-radius: 6px !important }`, so we mirror the effective
 * value, not the stock one), same elevation shadows lifted from the
 * Blueprint CSS.
 *
 * Supported props:
 *   - elevation        → 0..4 (use the `Elevation` re-export for ergonomic
 *                        equivalents to Blueprint's enum)
 *   - interactive      → adds hover/focus shadow + cursor:pointer
 *   - className/style  → forwarded to the wrapper
 *   - all standard <div> HTML attributes via spread
 *
 * Usage audit (2026-05-07): 28 files, 68 <Card> tags. Real props in use are
 * `className` (63), `style` (5), `elevation` (4 — Elevation.ONE/TWO and
 * literal `2`), `key` (React, not a Card prop). No callsite uses
 * `interactive`, `onClick`, or `compact` — `interactive` is supported for API
 * parity, `compact` was dropped (Blueprint v5 prop, no usage here).
 *
 * Padding: this component does **not** apply a default padding. ~58/62 cards
 * already pass an explicit `p-*` className, and Bootstrap is loaded (it ships
 * `.p-N !important` which would clash with any default we'd emit). Callsites
 * that previously relied on Blueprint's stock 20px default were updated to
 * pass `p-[20px]` explicitly during the Tooltip→Card migration. See
 * `docs/blueprint-shadcn-migration.md` for the full Bootstrap collision
 * write-up.
 *
 * Note: many callsites pass `bp4-card-rounded` in className. That class has
 * no rule defined anywhere (neither Blueprint's CSS nor our app.scss), so it
 * had no effect before and continues to have no effect. Safe to leave.
 */

export type Elevation = 0 | 1 | 2 | 3 | 4

// Mirror Blueprint's `Elevation` enum so callsites importing it from this
// module get the same ergonomics as `import { Elevation } from '@blueprintjs/core'`.
export const Elevation = {
    ZERO: 0,
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
} as const

// Box-shadow values copied verbatim from
// node_modules/@blueprintjs/core/lib/css/blueprint.css `.bp4-elevation-N`.
const elevationShadow: Record<Elevation, string> = {
    0: 'shadow-[0_0_0_1px_rgba(17,20,24,0.15)]',
    1: 'shadow-[0_0_0_1px_rgba(17,20,24,0.1),0_1px_1px_rgba(17,20,24,0.2)]',
    2: 'shadow-[0_0_0_1px_rgba(17,20,24,0.1),0_1px_1px_rgba(17,20,24,0.2),0_2px_6px_rgba(17,20,24,0.2)]',
    3: 'shadow-[0_0_0_1px_rgba(17,20,24,0.1),0_2px_4px_rgba(17,20,24,0.2),0_8px_24px_rgba(17,20,24,0.2)]',
    4: 'shadow-[0_0_0_1px_rgba(17,20,24,0.1),0_4px_8px_rgba(17,20,24,0.2),0_18px_46px_6px_rgba(17,20,24,0.2)]',
}

// Hover-shadow values from `.bp4-card.bp4-interactive:hover`.
const interactiveHoverShadow =
    'hover:shadow-[0_0_0_1px_rgba(17,20,24,0.1),0_2px_4px_rgba(17,20,24,0.2),0_8px_24px_rgba(17,20,24,0.2)]'

// Active-shadow values from `.bp4-card.bp4-interactive:active`.
const interactiveActiveShadow =
    'active:shadow-[inset_0_0_0_1px_rgba(17,20,24,0.1),inset_0_1px_2px_rgba(17,20,24,0.2)]'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    elevation?: Elevation
    interactive?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
    { elevation = 0, interactive = false, className, children, onClick, onKeyDown, ...rest },
    ref,
) {
    // When `interactive` is set, treat the card as a button: keyboard users
    // need to be able to activate it with Enter/Space, and screen readers
    // need a button role. Blueprint's Card itself doesn't ship a11y
    // helpers, so this is defensive — no callsite uses `interactive` today
    // but the prop is exposed and future use shouldn't ship inaccessible.
    const handleKeyDown = interactive
        ? (e: React.KeyboardEvent<HTMLDivElement>) => {
              onKeyDown?.(e)
              if (e.defaultPrevented) return
              if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>)
              }
          }
        : onKeyDown

    const a11yProps = interactive ? { role: 'button' as const, tabIndex: 0 } : {}

    return (
        <div
            ref={ref}
            className={classNames(
                // Base: matches `.bp4-card` after the app.scss override
                // (rounded-md = 6px). No padding by default — see header.
                'bg-white rounded-md transition-shadow duration-200',
                elevationShadow[elevation],
                interactive && [
                    'cursor-pointer',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d72d2] focus-visible:ring-offset-1',
                    interactiveHoverShadow,
                    interactiveActiveShadow,
                ],
                className,
            )}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            {...a11yProps}
            {...rest}
        >
            {children}
        </div>
    )
})

export { Card }
